import { Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Menu, Transition } from '@headlessui/react';
import { BellIcon, CheckIcon } from '@heroicons/react/24/outline';
import { notificationsApi, Notification } from '@/lib/api';
import { formatDistanceToNow } from 'date-fns';

export default function NotificationBell() {
  const queryClient = useQueryClient();

  // Fetch unread count
  const { data: countData } = useQuery({
    queryKey: ['notifications-unread-count'],
    queryFn: async () => {
      const res = await notificationsApi.getUnreadCount();
      return res.data.data?.count ?? 0;
    },
    refetchInterval: 30000, // Poll every 30 seconds
  });

  // Fetch recent notifications for dropdown
  const { data: notificationsData } = useQuery({
    queryKey: ['notifications-recent'],
    queryFn: async () => {
      const res = await notificationsApi.list({ limit: 5 });
      return res.data.data?.notifications ?? [];
    },
    refetchInterval: 30000,
  });

  const unreadCount = countData ?? 0;
  const notifications = notificationsData ?? [];

  const handleMarkAsRead = async (id: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await notificationsApi.markAsRead(id);
    queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    queryClient.invalidateQueries({ queryKey: ['notifications-recent'] });
  };

  const handleMarkAllAsRead = async () => {
    await notificationsApi.markAllAsRead();
    queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    queryClient.invalidateQueries({ queryKey: ['notifications-recent'] });
  };

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'share':
        return '🔗';
      case 'mention':
        return '@';
      case 'expiry':
        return '⏰';
      case 'access':
        return '👁';
      case 'system':
        return '⚙️';
      default:
        return '📬';
    }
  };

  return (
    <Menu<"div"> as="div" className="relative">
      <Menu.Button className="relative p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
        <BellIcon className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-medium text-white bg-red-500 rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </Menu.Button>

      <Transition
       
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 mt-2 w-80 origin-top-right rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-hidden z-50">
          <div className="p-3 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-xs text-primary-600 hover:text-primary-700"
                >
                  Mark all as read
                </button>
              )}
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-8 text-center">
                <BellIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No notifications</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <Menu.Item key={notification.id}>
                  {({ active }: { active: boolean }) => (
                    <div
                      className={`relative block px-4 py-3 ${
                        active ? 'bg-gray-50' : ''
                      } ${!notification.read ? 'bg-blue-50' : ''}`}
                    >
                      {notification.link ? (
                        <Link to={notification.link} className="block">
                          <NotificationContent
                            notification={notification}
                            getIcon={getNotificationIcon}
                          />
                        </Link>
                      ) : (
                        <NotificationContent
                          notification={notification}
                          getIcon={getNotificationIcon}
                        />
                      )}
                      {!notification.read && (
                        <button
                          onClick={(e) => handleMarkAsRead(notification.id, e)}
                          className="absolute top-3 right-3 p-1 text-gray-400 hover:text-primary-600 hover:bg-gray-100 rounded"
                          title="Mark as read"
                        >
                          <CheckIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  )}
                </Menu.Item>
              ))
            )}
          </div>

          <div className="p-2 border-t border-gray-100">
            <Link
              to="/notifications"
              className="block text-center text-sm text-primary-600 hover:text-primary-700 py-1"
            >
              View all notifications
            </Link>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

function NotificationContent({
  notification,
  getIcon,
}: {
  notification: Notification;
  getIcon: (type: Notification['type']) => string;
}) {
  return (
    <div className="flex gap-3">
      <span className="flex-shrink-0 text-lg">
        {getIcon(notification.type)}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">
          {notification.title}
        </p>
        <p className="text-xs text-gray-500 line-clamp-2">
          {notification.message}
        </p>
        <p className="text-xs text-gray-400 mt-1">
          {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
        </p>
      </div>
    </div>
  );
}
